import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import GenerationModule from "../components/workspace/GenerationModule";
import ProjectPicker from "../components/ProjectPicker";

const STRATEGY_TOOLS = [
  { kind: "swot", label: "SWOT Analysis", desc: "Strengths, weaknesses, opportunities and threats — auto-tailored.", icon: "ShieldCheck", gradient: "from-[#7C3AED] to-[#EC4899]" },
  { kind: "business_model_canvas", label: "Business Model Canvas", desc: "The 9 sections of a working business model.", icon: "LayoutGrid", gradient: "from-[#6366F1] to-[#7C3AED]" },
  { kind: "go_to_market", label: "Go-to-Market", desc: "ICP, segments, positioning, funnels, retention — done.", icon: "Compass", gradient: "from-[#EC4899] to-[#A855F7]" },
  { kind: "customer_persona", label: "Customer Personas", desc: "Two detailed personas with goals and objections.", icon: "Users", gradient: "from-[#22C55E] to-[#6366F1]" },
  { kind: "competitor_analysis", label: "Competitor Analysis", desc: "Matrix, gaps and opportunities versus 5 competitors.", icon: "Search", gradient: "from-[#A855F7] to-[#F59E0B]" },
];

const MARKETING_TOOLS = [
  { kind: "marketing_plan", label: "1-Page Marketing Plan", desc: "Objectives, channels, KPIs, budget and 90-day timeline.", icon: "Megaphone", gradient: "from-[#EC4899] to-[#A855F7]" },
  { kind: "brand_strategy", label: "Brand Strategy", desc: "Story, voice, positioning, taglines and personality.", icon: "Palette", gradient: "from-[#7C3AED] to-[#EC4899]" },
  { kind: "customer_persona", label: "Customer Personas", desc: "Two rich personas to anchor every campaign.", icon: "Users", gradient: "from-[#22C55E] to-[#6366F1]" },
];

export default function StrategyMarketingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState("strategy");

  useEffect(() => {
    if (tabParam === "marketing") {
      setActiveTab("marketing");
    } else {
      setActiveTab("strategy");
    }
  }, [tabParam]);

  return (
    <div className="space-y-6 pb-12">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">Workspace</p>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tighter text-gray-900">
            Strategy & Marketing
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Create your business strategy and marketing plan from one unified workspace.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start md:self-auto">
          <ProjectPicker />
        </div>
      </div>

      {/* Tabs Selector */}
      <div className="flex items-center gap-1.5 p-1 rounded-xl bg-gray-100/80 max-w-xs">
        <button
          onClick={() => {
            setActiveTab("strategy");
            setSearchParams({ tab: "strategy" });
          }}
          className={`flex-1 py-2 px-4 text-center text-xs font-semibold rounded-lg transition-all ${
            activeTab === "strategy"
              ? "bg-white text-gray-900 shadow-xs"
              : "text-gray-500 hover:text-gray-900"
          }`}
        >
          Strategy
        </button>
        <button
          onClick={() => {
            setActiveTab("marketing");
            setSearchParams({ tab: "marketing" });
          }}
          className={`flex-1 py-2 px-4 text-center text-xs font-semibold rounded-lg transition-all ${
            activeTab === "marketing"
              ? "bg-white text-gray-900 shadow-xs"
              : "text-gray-500 hover:text-gray-900"
          }`}
        >
          Marketing
        </button>
      </div>

      {/* Strategy Content */}
      <div className={activeTab === "strategy" ? "block" : "hidden"}>
        <GenerationModule
          title="Strategy"
          eyebrow="Module"
          intro="Frameworks, analyses and roadmaps — generated, refined and ready in seconds."
          tools={STRATEGY_TOOLS}
        />
      </div>

      {/* Marketing Content */}
      <div className={activeTab === "marketing" ? "block" : "hidden"}>
        <GenerationModule
          title="Marketing"
          eyebrow="Module"
          intro="A studio for your brand, plan and audience — in one canvas."
          tools={MARKETING_TOOLS}
        />
      </div>
    </div>
  );
}
